import { RefObject } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { NudgeHint } from "@/components/nudge-hint";
import { StudioAdvancedPanel } from "@/components/studio-advanced-panel";
import { VoiceInputButton, InlineRecordingIndicator } from "@/components/voice-input-button";
import { Sparkles, Loader2, Lightbulb, Target, X } from "lucide-react";
import type { Preset } from "@/lib/studio-presets";
import type { UseInlineVoiceInputReturn } from "@/hooks/use-inline-voice-input";
import type { SavedPreset } from "@/lib/studio-config";

interface StudioInputPanelProps {
  textareaRef: RefObject<HTMLTextAreaElement>;
  generateBtnRef: RefObject<HTMLButtonElement>;
  request: string;
  handleRequestChange: (v: string) => void;
  copilotHint: string | null;
  setCopilotHint: (v: string | null) => void;
  handleGenerate: () => void;
  isGenerating: boolean;
  placeholder: string;
  isRequestVague: boolean;
  studioVoiceInput: UseInlineVoiceInputReturn;
  setRequest: (v: string | ((prev: string) => string)) => void;
  ghostDraftLoading: boolean;
  handleGhostDraft: () => void;
  selectedPreset: Preset | null;
  inferredIntent: string | null;
  setInferredIntent: (v: string | null) => void;
  intentEditing: boolean;
  setIntentEditing: (v: boolean) => void;
  intentEditValue: string;
  setIntentEditValue: (v: string) => void;
  studioAdvancedOpen: boolean;
  setStudioAdvancedOpen: (v: boolean) => void;
  studioAdvancedCount: number;
  advancedEverOpened: boolean;
  setAdvancedEverOpened: (v: boolean) => void;
  voiceStates: Array<{ label: string; description: string; dominantTone: string; exampleContext: string }> | undefined;
  studioVoiceState: string | undefined;
  setStudioVoiceState: (v: string | undefined) => void;
  studioAudienceDescription: string;
  setStudioAudienceDescription: (v: string) => void;
  studioTone: number;
  setStudioTone: (v: number) => void;
  studioPostStyle: string | undefined;
  setStudioPostStyle: (v: string | undefined) => void;
  studioHookStyle: string | undefined;
  setStudioHookStyle: (v: string | undefined) => void;
  studioSpacingStyle: string | undefined;
  setStudioSpacingStyle: (v: string | undefined) => void;
  studioPerspective: string | undefined;
  setStudioPerspective: (v: string | undefined) => void;
  studioHumorLevel: string | undefined;
  setStudioHumorLevel: (v: string | undefined) => void;
  studioAudience: string | undefined;
  setStudioAudience: (v: string | undefined) => void;
  studioEmojiUsage: string | undefined;
  setStudioEmojiUsage: (v: string | undefined) => void;
  studioTargetWordCount: string;
  setStudioTargetWordCount: (v: string) => void;
  studioCustomInstruction: string;
  setStudioCustomInstruction: (v: string) => void;
  studioBlendStyle: string | undefined;
  setStudioBlendStyle: (v: string | undefined) => void;
  studioRecentTopics: Array<{ topic: string; format: string }>;
  clearStudioContext: () => void;
  clearStudioAdvanced: () => void;
  savePresetInputOpen: boolean;
  setSavePresetInputOpen: (v: boolean) => void;
  savePresetName: string;
  setSavePresetName: (v: string) => void;
  savingPreset: boolean;
  handleSavePreset: () => void;
}

export function StudioInputPanel({
  textareaRef, generateBtnRef,
  request, handleRequestChange, copilotHint, setCopilotHint,
  handleGenerate, isGenerating, placeholder, isRequestVague,
  studioVoiceInput, setRequest,
  ghostDraftLoading, handleGhostDraft, selectedPreset,
  inferredIntent, setInferredIntent, intentEditing, setIntentEditing,
  intentEditValue, setIntentEditValue,
  studioAdvancedOpen, setStudioAdvancedOpen, studioAdvancedCount,
  advancedEverOpened, setAdvancedEverOpened,
  voiceStates, studioVoiceState, setStudioVoiceState,
  studioAudienceDescription, setStudioAudienceDescription,
  studioTone, setStudioTone,
  studioPostStyle, setStudioPostStyle,
  studioHookStyle, setStudioHookStyle,
  studioSpacingStyle, setStudioSpacingStyle,
  studioPerspective, setStudioPerspective,
  studioHumorLevel, setStudioHumorLevel,
  studioAudience, setStudioAudience,
  studioEmojiUsage, setStudioEmojiUsage,
  studioTargetWordCount, setStudioTargetWordCount,
  studioCustomInstruction, setStudioCustomInstruction,
  studioBlendStyle, setStudioBlendStyle,
  studioRecentTopics, clearStudioContext, clearStudioAdvanced,
  savePresetInputOpen, setSavePresetInputOpen,
  savePresetName, setSavePresetName,
  savingPreset, handleSavePreset,
}: StudioInputPanelProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.08 }}
      className="mb-4"
    >
      <div className="rounded-xl border border-border/50 bg-card overflow-hidden shadow-sm">
        <div className="px-4 pt-3.5 pb-1 relative">
          <Textarea
            ref={textareaRef}
            value={request}
            onChange={(e) => handleRequestChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Tab" && copilotHint) {
                e.preventDefault();
                const sep = request.endsWith(" ") ? "" : " ";
                handleRequestChange(request + sep + copilotHint);
                setCopilotHint(null);
                return;
              }
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleGenerate();
              }
              if (copilotHint && e.key !== "Tab") {
                setCopilotHint(null);
              }
            }}
            placeholder={placeholder}
            className="min-h-[90px] border-0 bg-transparent p-0 text-sm resize-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/35"
          />
          <AnimatePresence>
            {copilotHint && (
              <motion.div
                key="copilot-hint"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="flex items-center gap-1.5 mt-1"
              >
                <span className="text-xs text-muted-foreground/30 italic">{copilotHint}</span>
                <span className="text-[10px] text-muted-foreground/25 border border-muted-foreground/20 rounded px-1 py-0.5 font-mono">Tab</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="px-4 pb-1">
          <NudgeHint isVague={isRequestVague} sessionDismissKey="studio" />
        </div>

        {selectedPreset && request.trim().length === 0 && (
          <div className="px-4 pb-2">
            <AnimatePresence>
              <motion.button
                key="ghost-draft-btn"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                type="button"
                onClick={handleGhostDraft}
                disabled={ghostDraftLoading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed border-primary/30 bg-primary/5 text-xs font-medium text-primary/60 hover:text-primary hover:border-primary/50 hover:bg-primary/10 transition-all duration-200 disabled:opacity-50"
              >
                {ghostDraftLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {ghostDraftLoading ? "Writing..." : "Start from scratch — let AI choose the topic"}
              </motion.button>
            </AnimatePresence>
          </div>
        )}

        {selectedPreset && request.trim().length > 0 && (
          <div className="px-4 pb-2">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/50">
              <Lightbulb className="w-3 h-3 shrink-0" />
              {selectedPreset.hint}
            </div>
          </div>
        )}

        <AnimatePresence>
          {inferredIntent && !intentEditing && (
            <motion.div
              key="intent-chip"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.2 }}
              className="px-4 pb-2 flex items-center gap-1.5"
            >
              <span className="text-[10px] text-muted-foreground/40 uppercase tracking-widest font-bold">Goal</span>
              <button
                type="button"
                onClick={() => { setIntentEditing(true); setIntentEditValue(inferredIntent); }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/8 border border-primary/20 text-[11px] font-medium text-primary/60 hover:text-primary hover:border-primary/40 transition-all"
              >
                <Target className="w-3 h-3 shrink-0" />
                {inferredIntent}
              </button>
              <button
                type="button"
                onClick={() => setInferredIntent(null)}
                className="text-muted-foreground/30 hover:text-muted-foreground/60 transition-colors"
                aria-label="Dismiss intent"
              >
                <X className="w-3 h-3" />
              </button>
            </motion.div>
          )}
          {intentEditing && (
            <motion.div
              key="intent-edit"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="px-4 pb-2 flex items-center gap-1.5"
            >
              <span className="text-[10px] text-muted-foreground/40 uppercase tracking-widest font-bold">Goal</span>
              <input
                autoFocus
                value={intentEditValue}
                onChange={(e) => setIntentEditValue(e.target.value.slice(0, 60))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { setInferredIntent(intentEditValue.trim() || null); setIntentEditing(false); }
                  if (e.key === "Escape") { setIntentEditing(false); }
                }}
                onBlur={() => { setInferredIntent(intentEditValue.trim() || null); setIntentEditing(false); }}
                className="text-[11px] font-medium text-primary/70 bg-primary/8 border border-primary/30 rounded-full px-2 py-0.5 outline-none focus:border-primary/50 w-40"
              />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="px-4 pb-1.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <AnimatePresence>
              {studioVoiceInput.isRecording && (
                <InlineRecordingIndicator
                  recordingTime={studioVoiceInput.recordingTime}
                  formatTime={studioVoiceInput.formatTime}
                />
              )}
            </AnimatePresence>
            <VoiceInputButton
              voiceInput={studioVoiceInput}
              onTranscript={(text) => setRequest((prev) => prev ? prev + " " + text : text)}
              showLabel
              label="Record your idea"
            />
          </div>
          <span className={`text-[10px] tabular-nums ${
            request.length > 1150 ? "text-red-400" :
            request.length > 1000 ? "text-amber-400" :
            "text-muted-foreground/30"
          }`}>
            {request.length} / 1200
          </span>
        </div>

        <StudioAdvancedPanel
          studioAdvancedOpen={studioAdvancedOpen}
          setStudioAdvancedOpen={setStudioAdvancedOpen}
          studioAdvancedCount={studioAdvancedCount}
          advancedEverOpened={advancedEverOpened}
          setAdvancedEverOpened={setAdvancedEverOpened}
          request={request}
          voiceStates={voiceStates}
          studioVoiceState={studioVoiceState}
          setStudioVoiceState={setStudioVoiceState}
          studioAudienceDescription={studioAudienceDescription}
          setStudioAudienceDescription={setStudioAudienceDescription}
          studioTone={studioTone}
          setStudioTone={setStudioTone}
          studioPostStyle={studioPostStyle}
          setStudioPostStyle={setStudioPostStyle}
          studioHookStyle={studioHookStyle}
          setStudioHookStyle={setStudioHookStyle}
          studioSpacingStyle={studioSpacingStyle}
          setStudioSpacingStyle={setStudioSpacingStyle}
          studioPerspective={studioPerspective}
          setStudioPerspective={setStudioPerspective}
          studioHumorLevel={studioHumorLevel}
          setStudioHumorLevel={setStudioHumorLevel}
          studioAudience={studioAudience}
          setStudioAudience={setStudioAudience}
          studioEmojiUsage={studioEmojiUsage}
          setStudioEmojiUsage={setStudioEmojiUsage}
          studioTargetWordCount={studioTargetWordCount}
          setStudioTargetWordCount={setStudioTargetWordCount}
          studioCustomInstruction={studioCustomInstruction}
          setStudioCustomInstruction={setStudioCustomInstruction}
          studioBlendStyle={studioBlendStyle}
          setStudioBlendStyle={setStudioBlendStyle}
          studioRecentTopics={studioRecentTopics}
          clearStudioContext={clearStudioContext}
          clearStudioAdvanced={clearStudioAdvanced}
          savePresetInputOpen={savePresetInputOpen}
          setSavePresetInputOpen={setSavePresetInputOpen}
          savePresetName={savePresetName}
          setSavePresetName={setSavePresetName}
          savingPreset={savingPreset}
          handleSavePreset={handleSavePreset}
        />

        <div className="px-3 py-2.5 border-t border-border/30 flex items-center justify-between">
          <p className="text-[11px] text-muted-foreground/40 hidden sm:block">⌘↵ to generate</p>
          <Button
            ref={generateBtnRef}
            onClick={handleGenerate}
            disabled={isGenerating || request.trim().length < 5}
            size="sm"
            className={`ml-auto h-8 px-4 rounded-lg font-semibold text-xs btn-shimmer${isGenerating ? " pulse-ring" : ""}`}
            style={{
              background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))",
              color: "white",
            }}
          >
            {isGenerating ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Writing...</>
            ) : (
              <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Generate</>
            )}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
